import React, { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { MainPagesSectionHeader } from "./components/MainPagesSectionHeader";
import { CollaboratorService } from "../../services";
import { transformCloudinaryUrlTo4x3 } from "../../shared/helpers";
import { getJobTitleChipColor } from "../../shared/constants/job-category-colors";

interface Collaborator {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  textPresentation?: string;
  imgUrl?: string;
  images?: Array<{ url: string; isMain: boolean }>;
}

interface ExtendedCollaborator extends Collaborator {
  sortingOrder?: number;
  jobTitle?: string;
}

const PLACEHOLDER_IMAGE =
  "https://upload.wikimedia.org/wikipedia/commons/b/bc/Unknown_person.jpg";

const safeCloudinaryTransform = (url?: string): string => {
  if (!url) return PLACEHOLDER_IMAGE;
  try {
    return transformCloudinaryUrlTo4x3(url);
  } catch {
    return url;
  }
};

const resolveImageUrl = (collab: Collaborator): string => {
  const main =
    collab.images?.find((img) => img.isMain)?.url ??
    collab.images?.[0]?.url ??
    collab.imgUrl;
  return safeCloudinaryTransform(main);
};

export const MainPagesTeam: React.FC = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [collaborators, setCollaborators] = useState<ExtendedCollaborator[]>(
    []
  );

  const collaboratorService = new CollaboratorService();

  const fetchCollaborators = async () => {
    const fetched =
      (await collaboratorService.getCollaboratorsForWeb()) as unknown as ExtendedCollaborator[];
    const sorted = [...fetched].sort(
      (a, b) => (a?.sortingOrder ?? Infinity) - (b?.sortingOrder ?? Infinity)
    );
    setCollaborators(sorted);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchCollaborators();
  }, [dispatch]);

  return (
    <Fragment>
      <Box
        component="section"
        className="u-bgPrimaryLightest"
        sx={{ py: { xs: 6, md: 10 } }}
      >
        <MainPagesSectionHeader title="Equipo" />

        <Container maxWidth="lg" sx={{ mt: { xs: 4, md: 6 } }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={4}>
              {collaborators.map((collaborator) => {
                const imgUrl = resolveImageUrl(collaborator);
                const fullName = `${collaborator.first_name} ${collaborator.last_name}`;
                const jobTitle = collaborator.jobTitle;

                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={collaborator.id}
                    sx={{ display: "flex" }}
                  >
                    <Card
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 3,
                        overflow: "hidden",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={imgUrl}
                        alt={fullName}
                        sx={{
                          aspectRatio: "4 / 3",
                          objectFit: "cover",
                          objectPosition: "center top",
                          backgroundColor: "grey.100",
                        }}
                      />
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                          p: 3,
                        }}
                      >
                        <Stack spacing={0.75}>
                          <Typography
                            component="h3"
                            sx={{
                              fontSize: 22,
                              fontWeight: 700,
                              lineHeight: 1.25,
                              color: "text.primary",
                            }}
                          >
                            {fullName}
                          </Typography>
                          {jobTitle && (() => {
                            const chip = getJobTitleChipColor(jobTitle);
                            const color =
                              chip === "default" ? "text.secondary" : `${chip}.main`;
                            return (
                              <Typography
                                sx={{
                                  color,
                                  fontWeight: 600,
                                  letterSpacing: 0.5,
                                  textTransform: "uppercase",
                                  fontSize: 14,
                                }}
                              >
                                {jobTitle}
                              </Typography>
                            );
                          })()}
                        </Stack>

                        {collaborator.textPresentation && (
                          <Typography
                            color="text.secondary"
                            sx={{
                              mt: 1,
                              whiteSpace: "pre-line",
                              lineHeight: 1.6,
                              fontSize: 15,
                            }}
                          >
                            {collaborator.textPresentation}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </Box>
    </Fragment>
  );
};
